import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '../../components/ui'
import { TruckIcon, ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { easyparcelService, EasyParcelStatus } from '../../services/easyparcel.service'
import toast from 'react-hot-toast'

interface WalletEntry {
  balance?: number;
  currency?: string;
}

// EasyParcel wallet response: { data: { wallet: [{balance, currency}],
// free_credit_wallet: [{balance, currency}] } } — render as readable lines.
function formatWalletLines(wallet: unknown): string[] {
  if (wallet == null) return [];
  if (typeof wallet === 'object' && 'error' in (wallet as Record<string, unknown>)) {
    return [`Wallet lookup failed: ${(wallet as Record<string, unknown>).error}`];
  }

  const formatEntry = (label: string, entry: WalletEntry) => {
    const currency = entry.currency ?? 'MYR';
    const amount =
      typeof entry.balance === 'number' ? entry.balance.toFixed(2) : String(entry.balance ?? '—');
    return `${label}: ${currency} ${amount}`;
  };

  const data = (wallet as Record<string, any>).data ?? wallet;
  const lines: string[] = [];
  if (Array.isArray(data?.wallet)) {
    data.wallet.forEach((entry: WalletEntry) => lines.push(formatEntry('Wallet balance', entry)));
  }
  if (Array.isArray(data?.free_credit_wallet)) {
    data.free_credit_wallet.forEach((entry: WalletEntry) =>
      lines.push(formatEntry('Free credit', entry)),
    );
  }
  return lines.length > 0 ? lines : [`Wallet balance: ${JSON.stringify(wallet)}`];
}

export default function AdminIntegrationsPage() {
  const [searchParams] = useSearchParams()
  const oauthResult = searchParams.get('easyparcel') // 'connected' | 'error' from the OAuth callback
  const oauthReason = searchParams.get('reason')

  const [status, setStatus] = useState<EasyParcelStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)

  const loadStatus = async () => {
    setLoading(true)
    try {
      setStatus(await easyparcelService.getStatus())
    } catch (error: any) {
      console.error('Failed to load EasyParcel status:', error)
      toast.error(error.response?.data?.message || 'Failed to load integration status')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStatus()
  }, [])

  const handleConnect = async () => {
    try {
      setConnecting(true)
      const { url } = await easyparcelService.getAuthorizeUrl()
      window.location.href = url
    } catch (error: any) {
      console.error('Failed to start EasyParcel connection:', error)
      toast.error(error.response?.data?.message || 'Failed to start connection')
      setConnecting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Integrations</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Connect external services used by the platform.
        </p>

        {oauthResult === 'connected' && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950 p-4 text-sm text-green-700 dark:text-green-300">
            <CheckCircleIcon className="h-5 w-5" />
            EasyParcel connected successfully.
          </div>
        )}
        {oauthResult === 'error' && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 p-4 text-sm text-red-700 dark:text-red-300">
            <ExclamationTriangleIcon className="h-5 w-5" />
            EasyParcel connection failed{oauthReason ? `: ${oauthReason}` : ''}. Please try again.
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <TruckIcon className="h-5 w-5" />
                EasyParcel
              </span>
              {status && (
                <Badge variant={status.connected ? 'success' : 'secondary'}>
                  {status.connected ? 'Connected' : 'Not connected'}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading status…</p>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Courier shipping for the souvenir shop: live rates, label purchase, and
                  tracking via the EasyParcel Open API.
                </p>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <p>
                    Environment:{' '}
                    <span className="font-medium text-gray-900 dark:text-white">
                      {status?.environment || 'unknown'}
                    </span>
                  </p>
                  {status?.connected && (
                    <p>
                      Access token expires:{' '}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {status.accessTokenExpiresAt
                          ? new Date(status.accessTokenExpiresAt).toLocaleString()
                          : 'unknown'}
                      </span>
                    </p>
                  )}
                  {status?.connected && status.wallet != null && (
                    formatWalletLines(status.wallet).map((line) => (
                      <p key={line}>{line}</p>
                    ))
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Button onClick={handleConnect} disabled={connecting}>
                    <ArrowPathIcon className={`h-4 w-4 mr-2 ${connecting ? 'animate-spin' : ''}`} />
                    {connecting
                      ? 'Redirecting…'
                      : status?.connected
                        ? 'Reconnect EasyParcel'
                        : 'Connect EasyParcel'}
                  </Button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    You will be redirected to EasyParcel to authorize access.
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
